import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { appelApi } from './api';
import type { CanalNotification, RegleAlerte } from './useAlertes';
import type { SeuilAbsenteisme } from './useApprenantsRisque';
import { CLE_MARQUE, type NuancesMarque } from '../marque/marque';
import type { VueReglagesReclamations } from './useReclamations';

/**
 * Paramétrage de la plateforme (TF-435) : les réglages généraux de
 * l'organisme, réservés à la direction et tracés. L'écran affiche ce que l'API
 * rend — bornes, défauts, énoncés — sans rien recalculer.
 */

export interface ToleranceRetard {
  minutes: number;
  texte: string;
  parDefaut: boolean;
  max: number;
  regleLe: string | null;
  reglePar: string | null;
}

/** Rappels d'émargement de l'organisme (TF-460). */
export interface RappelsEmargement {
  heureMatin: string;
  heureApresMidi: string;
  sms: boolean;
  parDefaut: boolean;
  regleLe: string | null;
  reglePar: string | null;
  plages: Record<'MATIN' | 'APRES_MIDI', { min: string; max: string }>;
  /** Le SMS part vraiment ; sinon il est tracé sans envoi. */
  smsRaccorde: boolean;
  pushRaccorde: boolean;
}

export interface SeuilFormation {
  id: string;
  code: string;
  titre: string;
  actif: boolean;
  seuil: number;
  dossiersSuivis: number;
}

export interface VersionModele {
  id: string;
  nomFichier: string;
  tailleOctets: number;
  deposeLe: string;
  deposePar: string | null;
}

export interface ModeleDepose {
  code: string;
  libelle: string;
  enVigueur: VersionModele | null;
  versions: VersionModele[];
}

export type OrigineModele = 'MODELE_DEPOSE' | 'FORMULAIRE_OFFICIEL' | 'MODELE_REPRODUIT' | 'CHARTE';

export interface DocumentProduit {
  libelle: string;
  origine: Exclude<OrigineModele, 'MODELE_DEPOSE'>;
  origineLibelle: string;
  source: string;
  ou: string;
}

export interface AutreReglage {
  libelle: string;
  detail: string;
  ecran: string;
  lien: string;
  tenuPar: string;
}

export interface VueParametrage {
  absences: {
    absenteisme: SeuilAbsenteisme;
    seuilParDefaut: number;
    min: number;
    max: number;
    formations: SeuilFormation[];
  };
  emargement: { tolerance: ToleranceRetard; rappels: RappelsEmargement };
  alertes: {
    regles: RegleAlerte[];
    sites: string[];
    profils: { valeur: string; libelle: string }[];
    canaux: { valeur: CanalNotification; libelle: string }[];
  };
  modeles: {
    deposes: ModeleDepose[];
    cerfa: { libelle: string; antenne: string; depose: boolean }[];
    formulaireRupture: { depose: boolean };
    produits: DocumentProduit[];
    origines: Record<OrigineModele, { libelle: string; detail: string }>;
  };
  marque: VueMarque;
  accessibilite: VueAccessibiliteParametrage;
  /** Responsable des réclamations de chaque antenne et délai visé (TF-482). */
  reclamations: VueReglagesReclamations;
  /** Mots et expressions bloqués dans les textes écrits pour d'autres (TF-484). */
  motsBloques: VueMotsBloques;
  autresReglages: AutreReglage[];
}

export interface MotBloque {
  id: string;
  mot: string;
  ajouteLe: string;
  ajoutePar: string | null;
}

export interface VueMotsBloques {
  mots: MotBloque[];
  /** Longueur d'une entrée, plafond de la liste, lettres avant un « * » final. */
  limites: { min: number; max: number; nombre: number; prefixe: number };
}

/** Référent handicap de l'organisme (TF-341), affiché sur l'écran « Accessibilité ». */
export interface ReferentHandicap {
  nom: string;
  email: string | null;
  telephone: string | null;
}

export interface VueAccessibiliteParametrage {
  referent: ReferentHandicap | null;
  limites: { nom: number; email: number; telephone: number };
  regleLe: string | null;
  reglePar: string | null;
}

export type CleCouleur = 'principale' | 'secondaire' | 'accent';
export type CouleursMarque = Record<CleCouleur, string>;

export interface Contraste {
  cle: string;
  libelle: string;
  rapport: number;
  texte: string;
  minimum: number;
  conforme: boolean;
}

/** Nuances et contrastes d'une palette, calculés par l'API (`POST /parametrage/marque/apercu`). */
export interface ApercuPalette {
  couleurs: CouleursMarque;
  nuances: NuancesMarque;
  contrastes: Contraste[];
  refus: string | null;
}

export type EtatDomaine = 'RACCORDE' | 'INTROUVABLE' | 'AUTRE_SERVEUR' | 'HTTPS_INDISPONIBLE' | 'AUTRE_PLATEFORME';

export interface VueDomaine {
  adresseActuelle: string;
  adresseParDefaut: string;
  cible: string;
  applique: { domaine: string; adresse: string; appliqueLe: string; appliquePar: string | null } | null;
  verification: { domaine: string; etat: EtatDomaine; libelle: string; detail: string; verifieLe: string } | null;
}

/** Onglet « Marque blanche » (TF-436). */
export interface VueMarque {
  nom: { valeur: string; charte: string; min: number; max: number };
  couleurs: { cle: CleCouleur; libelle: string; usage: string; valeur: string; charte: string }[];
  logo: { url: string; typeMime: string; taille: number; deposeLe: string; deposePar: string | null } | null;
  formatsLogo: string;
  palette: ApercuPalette;
  charte: { nom: string; couleurs: CouleursMarque; nuances: NuancesMarque };
  estCharte: boolean;
  modifieLe: string | null;
  modifiePar: string | null;
  domaine: VueDomaine;
  /** Page entreprise LinkedIn de l'organisme (TF-455) ; `null` : non réglée. */
  linkedin: { organisationId: string; page: string } | null;
}

export interface SaisieMarque {
  nom: string;
  couleurs: CouleursMarque;
  logo: 'CONSERVER' | 'RETIRER' | 'REMPLACER';
  fichier: File | null;
}

export interface LigneHistorique {
  id: string;
  survenuLe: string;
  action: string;
  actionLibelle: string;
  auteur: string | null;
  objet: string | null;
  changements: { champ: string; avant: string; apres: string }[];
}

export interface HistoriqueParametrage {
  lignes: LigneHistorique[];
  pagination: { page: number; taille: number; total: number; pages: number };
  actions: { code: string; libelle: string }[];
}

const CLE = ['parametrage'];

export function useParametrage() {
  return useQuery({
    queryKey: [...CLE, 'vue'],
    queryFn: () => appelApi<VueParametrage>('/parametrage'),
  });
}

export function useHistoriqueParametrage(filtres: { action: string; page: number; taille: number }, actif = true) {
  const parametres = new URLSearchParams({ page: String(filtres.page), taille: String(filtres.taille) });
  if (filtres.action) parametres.set('action', filtres.action);
  return useQuery({
    queryKey: [...CLE, 'historique', parametres.toString()],
    queryFn: () => appelApi<HistoriqueParametrage>(`/parametrage/historique?${parametres.toString()}`),
    placeholderData: keepPreviousData,
    enabled: actif,
  });
}

function invalider(client: ReturnType<typeof useQueryClient>): void {
  void client.invalidateQueries({ queryKey: CLE });
}

export function useReglerTolerance() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (minutes: number) =>
      appelApi<ToleranceRetard>('/parametrage/emargement', { methode: 'PUT', corps: { minutes } }),
    onSuccess: () => invalider(client),
  });
}

export function useReglerRappels() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (corps: { heureMatin: string; heureApresMidi: string; sms: boolean }) =>
      appelApi<RappelsEmargement>('/parametrage/emargement/rappels', { methode: 'PUT', corps }),
    onSuccess: () => invalider(client),
  });
}

export function useReglerSeuilFormation() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (variables: { formationId: string; seuil: number }) =>
      appelApi<{ formations: SeuilFormation[] }>(`/parametrage/absences/formations/${variables.formationId}`, {
        methode: 'PUT',
        corps: { seuil: variables.seuil },
      }),
    onSuccess: () => {
      invalider(client);
      // Le seuil se lit dans « Formations & programmes », « Absences » et la vue globale.
      void client.invalidateQueries({ queryKey: ['formations'] });
      void client.invalidateQueries({ queryKey: ['absences'] });
    },
  });
}

export function useReglerAbsenteisme() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (seuil: number) =>
      appelApi<SeuilAbsenteisme>('/apprenants-risque/seuil', { methode: 'PUT', corps: { seuil } }),
    onSuccess: () => {
      invalider(client);
      void client.invalidateQueries({ queryKey: ['apprenants-risque'] });
    },
  });
}

export function useDeposerModele() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (variables: { code: string; fichier: File }) => {
      const formulaire = new FormData();
      formulaire.append('fichier', variables.fichier);
      return appelApi<{ message: string; modeles: ModeleDepose[] }>(`/parametrage/modeles/${variables.code}`, {
        methode: 'POST',
        formulaire,
      });
    },
    onSuccess: () => {
      invalider(client);
      void client.invalidateQueries({ queryKey: ['documents-pedagogiques'] });
    },
  });
}

/** Couleur au format `#RRGGBB` : seule forme que l'aperçu envoie à l'API. */
export function estCouleur(valeur: string): boolean {
  return /^#[0-9a-fA-F]{6}$/.test(valeur.trim());
}

/** Aperçu d'une palette : nuances et contrastes, sans rien écrire. */
export function useApercuPalette(couleurs: CouleursMarque | null) {
  return useQuery({
    queryKey: [...CLE, 'marque', 'apercu', couleurs],
    queryFn: () =>
      appelApi<ApercuPalette>('/parametrage/marque/apercu', { methode: 'POST', corps: { couleurs } }),
    enabled: couleurs !== null,
    placeholderData: keepPreviousData,
    staleTime: Infinity,
  });
}

export function useAppliquerMarque() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (saisie: SaisieMarque) => {
      const formulaire = new FormData();
      formulaire.append(
        'donnees',
        JSON.stringify({ nom: saisie.nom, couleurs: saisie.couleurs, logo: saisie.logo }),
      );
      if (saisie.logo === 'REMPLACER' && saisie.fichier) formulaire.append('logo', saisie.fichier);
      return appelApi<VueMarque>('/parametrage/marque', { methode: 'PUT', formulaire });
    },
    onSuccess: () => {
      invalider(client);
      // Toute l'interface se rhabille : la marque publique est relue.
      void client.invalidateQueries({ queryKey: CLE_MARQUE });
    },
  });
}

export function useVerifierDomaine() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (domaine: string) =>
      appelApi<VueDomaine>('/parametrage/marque/domaine/verification', { methode: 'POST', corps: { domaine } }),
    onSettled: () => invalider(client),
  });
}

export function useAppliquerDomaine() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (domaine: string) =>
      appelApi<VueDomaine>('/parametrage/marque/domaine', { methode: 'PUT', corps: { domaine } }),
    // Refusé, le domaine a quand même été vérifié : l'écran relit l'issue.
    onSettled: () => invalider(client),
  });
}

/** Page LinkedIn de l'organisme (TF-455) : identifiant ou adresse ; vide, retirée. */
export function useReglerLinkedin() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (organisation: string) =>
      appelApi<VueMarque>('/parametrage/marque/linkedin', { methode: 'PUT', corps: { organisation } }),
    onSuccess: () => invalider(client),
  });
}

/** Tout vide : le référent est retiré et sa carte disparaît. */
export function useReglerReferentHandicap() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (saisie: { nom: string; email: string; telephone: string }) =>
      appelApi<VueAccessibiliteParametrage>('/parametrage/accessibilite/referent', { methode: 'PUT', corps: saisie }),
    onSuccess: () => {
      invalider(client);
      void client.invalidateQueries({ queryKey: ['accessibilite'] });
    },
  });
}

export function useRetirerDomaine() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: () => appelApi<VueDomaine>('/parametrage/marque/domaine', { methode: 'DELETE' }),
    onSuccess: () => invalider(client),
  });
}

/** Mots bloqués (TF-484) : un mot ou une expression de plus, « * » final pour tout mot qui commence pareil. */
export function useAjouterMotBloque() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (mot: string) => appelApi<VueMotsBloques>('/parametrage/mots-bloques', { methode: 'POST', corps: { mot } }),
    onSuccess: () => invalider(client),
  });
}

export function useRetirerMotBloque() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      appelApi<VueMotsBloques>(`/parametrage/mots-bloques/${encodeURIComponent(id)}`, { methode: 'DELETE' }),
    onSuccess: () => invalider(client),
  });
}

/** Ce que la liste bloquerait dans un texte : rien n'est enregistré. */
export function useEssaiMotsBloques() {
  return useMutation({
    mutationFn: (texte: string) =>
      appelApi<{ trouves: string[] }>('/parametrage/mots-bloques/apercu', { methode: 'POST', corps: { texte } }),
  });
}
